import { closePopup, getPopupRegistration, openPopup, Priority } from 'app/PopupHolder';
import classNames from "classnames";
import Modal from 'components/base/Modal';
import { ErrorMessage, Form, Formik } from "formik";
import React from 'react';
import { isValidEmail } from 'utils/validation';
import * as Yup from "yup";
import FormikText from '../components/Formik/FormikText';
import css from "../style.scss";

import config from 'config';
import { loadUserProfileSuccess } from "data/user/actions";
import { selectUserProfile } from "data/user/selectors";
import { useDispatch, useSelector } from 'react-redux';
import encryptPassword from '../../../utils/passwordValidation';
import LeaveMessagePopup from './LeaveMessage';
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { getProfileValidationRules } from '../../../utils/validation';
import { isEqual } from 'lodash';
import { useState, useEffect, useRef } from 'react';
import { getPasswordSchema } from '../../../components/PasswordStrengthIndecator/utils';
import PasswordStrength from '../../../components/PasswordStrengthIndecator';

const {
  constants: {
    API: { PRICING_UPDATE_URL },
  },
} = config;

const createDebouncedPasswordValidator = () => {
  let timeoutId = null;

  return (newPwd, confirmNewPwd, setFieldError, setFieldTouched, setPwdMatchStatus) => {
    if (timeoutId) clearTimeout(timeoutId);

    timeoutId = setTimeout(() => {
      if (!newPwd && !confirmNewPwd) {
        setPwdMatchStatus(null);
        setFieldError('confirmNewPwd', undefined);
        return;
      }

      if (!confirmNewPwd) {
        setPwdMatchStatus(null);
        setFieldError('confirmNewPwd', undefined);
        return;
      }

      setFieldTouched('confirmNewPwd', true, false);

      if (newPwd !== confirmNewPwd) {
        setPwdMatchStatus('mismatch');
        setFieldError('confirmNewPwd', 'Passwords Do Not Match');
      } else {
        setPwdMatchStatus('match');
        setFieldError('confirmNewPwd', undefined);
      }
    }, 800);
  };
};

const EditUserEmailPasswordPopup = (props) => {

  const dispatch = useDispatch();

  const immutableUserProfile = useSelector(selectUserProfile);
  const selectUserProfileSection = immutableUserProfile ? immutableUserProfile.toJS() : {};
  const validationRules = getProfileValidationRules();
  const [pwdMatchStatus, setPwdMatchStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const debouncedPasswordValidatorRef = useRef(createDebouncedPasswordValidator());

  // Validation schema
  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .nullable()
      .test('email-group-required', 'Email is required.', function (value) {
        const { confirmEmail } = this.parent;
        if (value || confirmEmail) {
          return !!value;
        }
        return true;
      })
      .test('is-valid-email', 'Email is invalid.', function (value) {
        if (!value) return true;
        return isValidEmail(value.trim());
      }),

    confirmEmail: Yup.string()
      .nullable()
      .test('confirm-email-required', 'Confirm Email is required.', function (value) {
        const { email } = this.parent;
        if (value || email) {
          return !!value;
        }
        return true;
      })
      .test('is-valid-confirm-email', 'Confirm Email is invalid.', function (value) {
        if (!value) return true;
        return isValidEmail(value.trim());
      })
      .test('email-match', 'Emails must match.', function (value) {
        const { email } = this.parent;
        if (value || email) {
          return value === email;
        }
        return true;
      }),

    currentPwd: Yup.string()
      .nullable()
      .test('password-group', 'Current Password is required.', function (value) {
        const { newPwd, confirmNewPwd } = this.parent;
        if (value || newPwd || confirmNewPwd) {
          return !!value;
        }
        return true;
      }),

    newPwd: getPasswordSchema(selectUserProfileSection?.username, selectUserProfileSection?.firstName, selectUserProfileSection?.lastName, null, null, null, true)
      .test(
        'password-confirm', 'Password required when current password is filled',
        function (value) {
          const { currentPwd } = this.parent;
          if (!value && currentPwd) {
            return false;
          }
          return true;
        }),


    confirmNewPwd: Yup.string()
      .nullable()
      .test('password-group', 'Confirm New Password is required', function (value) {
        const { currentPwd, newPwd } = this.parent;
        if (value || currentPwd || newPwd) {
          return !!value;
        }
        return true;
      })
      .test('passwords-match', 'Passwords Do Not Match', function (value) {
        const { newPwd } = this.parent;
        if (!value && !newPwd) return true;
        return value === newPwd;
      }),
  });
  const initialValues = {
    email: selectUserProfileSection?.username ? selectUserProfileSection?.username : null,
    confirmEmail: selectUserProfileSection?.username ? selectUserProfileSection?.username : null,
    currentPwd: null,
    newPwd: null,
    confirmNewPwd: null,
  };

  const handleClose = () => {
    props.closePopup();
  };


  const handleSubmit = (values) => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    let payload = {
      ...values,
      userId: selectUserProfileSection.userId,
      currentPwd: values.currentPwd ? encryptPassword(values.currentPwd) : null,
      newPwd: values.newPwd ? encryptPassword(values.newPwd) : null,
      confirmNewPwd: values.confirmNewPwd ? encryptPassword(values.confirmNewPwd) : null,
      email: values.email?.toLowerCase(),
      confirmEmail: values.confirmEmail?.toLowerCase(),
    }
    fetch(`${PRICING_UPDATE_URL}/ps/profile/email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-auth-token': window.appAuthToken
      },
      body: JSON.stringify(payload),
    })
      .then(async (response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          const errorResponse = await response.json();

          const isUsernameAlreadyUsed =
            errorResponse?.errorMessage.includes("Username") && errorResponse?.errorMessage.includes("already");
          let formattedError = 'Error'
          if (isUsernameAlreadyUsed) {
            formattedError = `System Error: ${errorResponse?.errorMessage}`
          } else {
            // Convert error object to readable message
            //  formattedError = Object.entries(errorResponse)
            //     .map(([field, message]) => `- ${message}`)
            //     .join('\n');
            formattedError = errorResponse?.errorMessage
          }

          throw new Error(formattedError);
        }
      })
      .then((data) => {
        console.log('Response from API:', data);
        dispatch(loadUserProfileSuccess(data));
        props.getUserProfile();
        handleClose()
      })
      .catch((error) => {
        console.error("Error occurred:", error.message);
        console.error('Error:', error);
        const isUsernameAlreadyUsed =
          error?.message.includes("Username") && error?.message.includes("already");

        if (isUsernameAlreadyUsed) {
          props.leaveMessagePopup({
            title: "Information",
            message: error.message,
            yesLable: "Ok",
            cancelLable: null,
            CancelRedirect: () => null,
            yesRedirect: () => null
          })

        } else {
          props.leaveMessagePopup({
            title: "Error",
            message: error.message,
            yesLable: "Ok",
            cancelLable: null,
            CancelRedirect: () => null,
            yesRedirect: () => null
          })
          
        }
        
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <Modal modalClassName={css.pricingModal} className={classNames(css.clsAccPageModals, css.clsBillingModalNew, css.clsAccUpdateProfileModal)} isOpen uniqId="EditUserEmailPasswordPopup" width="auto" padding="0px 0px 0px" onClose={handleClose} closeOnOutsideClick={false}>
      <div className={classNames(css.modal_lg, "modal-dialog modal-lg m-0")}>
        <div className={classNames(css.clsModalContent, "modal-content")}>
          <div className={classNames(css.clsModalContentBody, "modal-body")}>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={(values, { resetForm }) => {
                if (isEqual(values, initialValues)) {
                  // No change -> just close modal
                  handleClose();
                } else {

                  const allFieldsEmpty = Object.values(values).every(
                    (val) => val === null || val === ''
                  );

                  if (allFieldsEmpty) {
                    handleClose();
                    return;
                  }

                  // Proceed with submission
                  handleSubmit(values);
                }
              }}
            >
              {({ touched, errors, handleSubmit, setFieldValue, values, errorMessage, dirty, setFieldError, setFieldTouched }) => {

                useEffect(() => {
                  debouncedPasswordValidatorRef.current(
                    values.newPwd,
                    values.confirmNewPwd,
                    setFieldError,
                    setFieldTouched,
                    setPwdMatchStatus
                  );
                }, [values.newPwd, values.confirmNewPwd, setFieldError, setFieldTouched]);

                return (
                  <Form onSubmit={handleSubmit}>
                    <div className={css.clsModalHeader}>
                      <h2 className={css.clsModalTitle}>Update Email and Password</h2>
                    </div>

                    <div className={classNames(css.clsModalBody)}>
                      <div className="clearfix w-100">
                        <div className="clearfix">
                          <h4 className={css.clsRequiredTxt}>Indicates Required Field<span className={css.text_primary}>*</span></h4>
                          <h4 className={classNames(css.clsModalSubTitle2, css.pb_24, "text-left")}>Email/Username</h4>

                          <div className={classNames(css.row, "row")}>
                            <div className={classNames(css.col_lg_6, css.mb_lg_0, css.pb_24, "col-lg-6 col-12")}>
                              <div className={classNames(css.clsFormGroup)}>
                                <FormikText name="email" label="Email/Username" placeholder="Email Address" errors={errors} touched={touched} required={true}
                                  maxLength={validationRules.maxLength.email}
                                  validationPattern={validationRules.validationPattern.email}
                                />
                              </div>
                            </div>
                            <div className={classNames(css.col_lg_6, "col-lg-6 col-12")}>
                              <div className={classNames(css.clsFormGroup)}>
                                <FormikText name="confirmEmail" label="Confirm Email/Username" placeholder="Confirm Email Address" errors={errors} touched={touched} required={true}
                                  maxLength={validationRules.maxLength.email}
                                  validationPattern={validationRules.validationPattern.email}
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className={classNames(css.mt_48, "clearfix")}>
                          <h4 className={classNames(css.clsModalSubTitle2, css.pb_24, "text-left")}> Password </h4>

                          <div className={classNames(css.row, "row")}>
                            <div className={classNames(css.mb_24, css.col_lg_6, "col-lg-6 col-12")}>
                              <div className={css.clsFormGroup}>
                                <FormikText name="currentPwd" type="password" label="Enter Current Password" placeholder="*************" errors={errors} touched={touched} required={true} />
                                {/* <ErrorMessage name="currentPwd" component="p" className={css.clsFormErrorText} render={errorMessage} /> */}
                              </div>
                            </div>
                          </div>
                          <div className={classNames(css.row, "row")}>
                            <div className={classNames(css.col_lg_6, css.mb_lg_0, css.mb_24, "col-lg-6 col-12")}>
                              <div className={css.clsFormGroup}>
                                <FormikText name="newPwd" type="password" label="New Password" placeholder="*************" errors={errors} touched={touched} required={true} hideErrorMsg />
                                {/* <ErrorMessage name="newPwd" component="p" className={css.clsFormErrorText} render={errorMessage} /> */}

                              </div>
                              <PasswordStrength password={values.newPwd} />
                            </div>
                            <div className={classNames(css.col_lg_6, "col-lg-6 col-12")}>
                              <div className={css.clsFormGroup}>
                                <FormikText name="confirmNewPwd" type="password" label="Confirm New Password" placeholder="*************" errors={errors} touched={touched} delayOnChangeValidation={800} required={true} />
                                {/* <ErrorMessage name="confirmNewPwd" component="p" className={css.clsFormErrorText} render={errorMessage} /> */}
                                {pwdMatchStatus === 'match' && values.confirmNewPwd && (
                                  <div className={classNames(css.passwordMatchInfo, css.match)}>
                                    Passwords Match
                                  </div>
                                )}

                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className={classNames(css.clsModalFooter)}>
                      <button type="button" className={classNames(css.btn_lg, css.btn_primary, css.text_primary)}
                        onClick={() => {
                          if (dirty) {
                            props.leaveMessagePopup({
                              CancelRedirect: () => null,
                              yesRedirect: () => handleClose(),
                            })
                          } else {
                            handleClose();
                          }
                        }}> Cancel</button>
                      <button className={classNames(css.btn_lg, css.clsBtnOrng)} type="submit" >Save </button>
                    </div>

                  </Form>
                )
              }}
            </Formik>
          </div>

        </div>
      </div>

    </Modal >
  );
};

const EditUserEmailPasswordPopupRegistration = withRouter(connect(null, { leaveMessagePopup: LeaveMessagePopup.open })(EditUserEmailPasswordPopup));


const registrationId = getPopupRegistration(EditUserEmailPasswordPopupRegistration);
EditUserEmailPasswordPopup.open = (props = {}) => openPopup(registrationId, { ...props, priority: Priority.MEDIUM });
EditUserEmailPasswordPopup.close = () => closePopup({ popup: registrationId });

export default EditUserEmailPasswordPopup;